import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { fadeInUp, staggerContainer, scaleIn } from "@/motion/variants";
import SignalsSpreadCard from "@/components/charts/SignalsSpreadCard";
import SeasonalityCard from "@/components/charts/SeasonalityCard";
import FreightCard from "@/components/charts/FreightCard";
import { TradingPartnersTrustBar } from "@/components/TrustBar";
import Navigation from "@/components/Navigation";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { TradingTrustStrip } from "@/components/TradingTrustStrip";
import { FeatureBadges } from "@/components/MicroTrustBadges";
import { Package, DollarSign, Sparkles, Target, TrendingUp, Shield, FileText, CreditCard, Database, Ship, Receipt, Users, Search, CheckCircle, Award, Clock, ChevronDown, AlertTriangle, Download, Building2, FileCheck, Banknote } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const Trading = () => {

  return (
    <div className="min-h-screen pt-6">
      <SEO
        title="LC-Backed Commodity Trading | FOB to CFR Deals | Padmora"
        description="Convert FOB supply into CFR delivery using transferable/confirmed LCs, standard inspections, and shipbroker execution. UCP600 compliant with AI-powered document control."
        image="/og-trading.jpg"
        canonical="https://padmora.com/trading"
        keywords={[
          "LC-backed trading",
          "FOB to CFR conversion",
          "transferable letter of credit",
          "confirmed letter of credit",
          "commodity trading",
          "UCP600 compliance",
          "documentary credit",
          "trade finance",
          "shipping documents",
          "commodity deals",
          "ammonium sulphate trading",
          "shipbroker execution"
        ]}
        aiOptimization={{
          aiKeywords: [
            "automated LC processing",
            "AI document verification",
            "predictive trade analytics",
            "machine learning compliance",
            "intelligent trade execution"
          ],
          entities: [
            "Letter of Credit",
            "FOB",
            "CFR",
            "UCP600",
            "Ammonium Sulphate",
            "United States",
            "Trade Finance"
          ],
          topics: [
            "Commodity Trading",
            "Trade Finance",
            "Document Control",
            "Compliance Management",
            "Supply Chain Finance"
          ]
        }}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "LC-Backed Commodity Trading",
          "description": "Professional commodity trading services converting FOB supply to CFR delivery using transferable and confirmed letters of credit with full UCP600 compliance.",
          "provider": {
            "@type": "Organization",
            "name": "Padmora",
            "url": "https://padmora.com"
          },
          "serviceType": "Commodity Trading",
          "areaServed": [
            {
              "@type": "Country",
              "name": "United States"
            },
            {
              "@type": "Place",
              "name": "Latin America"
            }
          ]
        }}
        breadcrumbs={[
          { name: "Home", url: "https://padmora.com/" },
          { name: "Trading", url: "https://padmora.com/trading" }
        ]}
      />
      <Navigation />

      {/* Hero Section */}
      <section className="min-h-screen flex flex-col justify-center">
        <div className="container mx-auto px-4 py-20">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-6xl font-bold text-slate-900 mb-6"
            >
              LC-backed trading with document control
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-slate-600 mb-8"
            >
              We convert FOB supply into CFR delivery using transferable/confirmed LCs, standard inspections, and shipbroker execution.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="px-8 py-4 text-lg"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com"
                >
                  Send enquiry
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="px-8 py-4 text-lg"
                asChild
              >
                <Link to="/trading/amsul-usa" className="text-slate-700 hover:text-slate-900">
                  Check AMSUL → USA
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="px-8 py-4 text-lg"
                asChild
              >
                <Link to="/trading/palm-products" className="text-slate-700 hover:text-slate-900">
                  Check Palm → World
                </Link>
              </Button>
            </motion.div>
          </motion.div>

          <TradingTrustStrip />

          {/* Partners at bottom of hero */}
          <div className="mt-16">
            <TradingPartnersTrustBar />
          </div>
        </div>
      </section>

      {/* Three Columns */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto"
          >
            <motion.div
              variants={fadeInUp}
              className="text-left bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <h3 className="text-xl font-bold mb-6 text-slate-900">What we do</h3>
              <FeatureBadges
                features={[
                  { icon: <FileText className="h-4 w-4" />, label: "FOB→CFR structuring" },
                  { icon: <CreditCard className="h-4 w-4" />, label: "Transferable/Confirmed LC" },
                  { icon: <Shield className="h-4 w-4" />, label: "Back-to-Back when required" },
                  { icon: <CheckCircle className="h-4 w-4" />, label: "SGS & Insurance ICC(A)" },
                  { icon: <Ship className="h-4 w-4" />, label: "Chartering with vetted brokers" },
                  { icon: <Database className="h-4 w-4" />, label: "NDA-gated document room" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="text-left bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <h3 className="text-xl font-bold mb-6 text-slate-900">What we don't</h3>
              <FeatureBadges
                features={[
                  { icon: <TrendingUp className="h-4 w-4" />, label: "No advance cash" },
                  { icon: <FileText className="h-4 w-4" />, label: "No soft offers without LC language" },
                  { icon: <Shield className="h-4 w-4" />, label: "No off-platform document handling" },
                  { icon: <AlertTriangle className="h-4 w-4" />, label: "No sanctioned parties" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="text-left bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <h3 className="text-xl font-bold mb-6 text-slate-900">Instruments</h3>
              <FeatureBadges
                features={[
                  { icon: <CreditCard className="h-4 w-4" />, label: "LC at sight (UCP600)" },
                  { icon: <FileText className="h-4 w-4" />, label: "Transferable LC (preferred)" },
                  { icon: <Shield className="h-4 w-4" />, label: "Confirmed LC" },
                  { icon: <CheckCircle className="h-4 w-4" />, label: "SBLC (ISP98/URDG)" },
                  { icon: <Database className="h-4 w-4" />, label: "B2B LC if unavoidable" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Process (4 steps) */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="text-3xl font-bold text-center mb-12"
          >
            Our Process
          </motion.h2>
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto"
          >
            <motion.div
              variants={fadeInUp}
              className="text-center bg-white border border-border/50 p-6 rounded-lg shadow-sm"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <span className="text-lg font-bold text-blue-600">1</span>
              </div>
              <h3 className="text-lg font-semibold mb-3">Requirements & KYC</h3>
              <p className="text-slate-600 text-sm">Buyer/seller requirements and compliance verification</p>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="text-center bg-white border border-border/50 p-6 rounded-lg shadow-sm"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <span className="text-lg font-bold text-blue-600">2</span>
              </div>
              <h3 className="text-lg font-semibold mb-3">LC Language Pre-check</h3>
              <p className="text-slate-600 text-sm">Review with our US banking partner before issuance</p>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="text-center bg-white border border-border/50 p-6 rounded-lg shadow-sm"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <span className="text-lg font-bold text-blue-600">3</span>
              </div>
              <h3 className="text-lg font-semibold mb-3">Mirror Terms to Seller</h3>
              <p className="text-slate-600 text-sm">Transfer/sub-LC to secure supply terms</p>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="text-center bg-white border border-border/50 p-6 rounded-lg shadow-sm"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <span className="text-lg font-bold text-blue-600">4</span>
              </div>
              <h3 className="text-lg font-semibold mb-3">Document Presentation</h3>
              <p className="text-slate-600 text-sm">Bank presentation and payment execution</p>
            </motion.div>
          </motion.div>

          {/* CTA */}
          <motion.div variants={fadeInUp} className="text-center mt-12">
            <Button
              size="lg"
              className="px-8 py-4 text-lg bg-primary hover:bg-primary/90 text-white"
              asChild
            >
              <a
                href="/lc-policy"
              >
                Learn More About LC Policy
              </a>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Specimen Document Set */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-8"
            >
              Specimen Document Set
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-center text-slate-600 mb-12"
            >
              Illustrative documents with SPECIMEN watermark (not linked to actual transactions)
            </motion.p>
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              <motion.div
                variants={fadeInUp}
                className="bg-card/50 border border-border/50 p-6 rounded-lg shadow-sm text-center"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-3">LC MT700</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Specimen LC with key clauses and conditions
                </p>
                <Button variant="outline" size="sm" className="w-full" asChild>
                  <a href="/docs/Padmora_SPECIMEN_LC_MT700_Template_header.pdf" download target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </a>
                </Button>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card/50 border border-border/50 p-6 rounded-lg shadow-sm text-center"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <CheckCircle className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-3">SGS Scope</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Inspection certificate format and requirements
                </p>
                <Button variant="outline" size="sm" className="w-full" asChild>
                  <a href="/docs/Padmora_SPECIMEN_SGS_QQ_Scope_header.pdf" download target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </a>
                </Button>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card/50 border border-border/50 p-6 rounded-lg shadow-sm text-center"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Shield className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-3">Insurance ICC(A)</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Marine cargo insurance certificate format
                </p>
                <Button variant="outline" size="sm" className="w-full" asChild>
                  <a href="/docs/Padmora_SPECIMEN_Insurance_ICC_A_Certificate_header.pdf" download target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </a>
                </Button>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card/50 border border-border/50 p-6 rounded-lg shadow-sm text-center"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-3">Term Sheet</h3>
                <p className="text-slate-600 text-sm mb-4">
                  FOB→CFR transaction terms and conditions
                </p>
                <Button variant="outline" size="sm" className="w-full" asChild>
                  <a href="/docs/Padmora_SPECIMEN_Term_Sheet_FOB_to_CFR_header.pdf" download target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </a>
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* AI Signals section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="text-3xl font-bold text-center mb-4"
          >
            AI Signals (Illustrative)
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="text-center text-slate-600 mb-12 max-w-2xl mx-auto"
          >
            Predictive analytics and market intelligence powered by AI to inform your trading decisions
          </motion.p>
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid md:grid-cols-3 gap-8"
          >
            <SignalsSpreadCard />
            <SeasonalityCard />
            <FreightCard />
          </motion.div>
        </div>
      </section>


      {/* Service Packages */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="text-3xl font-bold text-center mb-12"
          >
            Service Packages
          </motion.h2>
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto"
          >
            <motion.div
              variants={fadeInUp}
              className="bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <h3 className="text-2xl font-bold mb-4 text-slate-900">For Sellers (FOB)</h3>
              <FeatureBadges
                features={[
                  { icon: <FileText className="h-4 w-4" />, label: "SCO/FCO & SPA preparation" },
                  { icon: <Search className="h-4 w-4" />, label: "Buyer discovery (KYC-ready)" },
                  { icon: <Shield className="h-4 w-4" />, label: "Sanctions screening" },
                  { icon: <Ship className="h-4 w-4" />, label: "Shipbroker fixture coordination" },
                  { icon: <Award className="h-4 w-4" />, label: "Insurance & inspection orchestration" },
                  { icon: <CreditCard className="h-4 w-4" />, label: "LC support (SBLC/DLC/at sight)" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>
            <motion.div
              variants={fadeInUp}
              className="bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <h3 className="text-2xl font-bold mb-4 text-slate-900">For Buyers (CFR/CIF)</h3>
              <FeatureBadges
                features={[
                  { icon: <Search className="h-4 w-4" />, label: "Multiorigin sourcing" },
                  { icon: <Target className="h-4 w-4" />, label: "Predictive indicators" },
                  { icon: <FileText className="h-4 w-4" />, label: "Term sheet & SPA templates" },
                  { icon: <CreditCard className="h-4 w-4" />, label: "LC coordination" },
                  { icon: <Clock className="h-4 w-4" />, label: "Laytime/demurrage guidance" },
                  { icon: <Database className="h-4 w-4" />, label: "Document tracking" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Document Center */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-8"
            >
              Standardized Documentation (Private)
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-center text-slate-600 mb-12"
            >
              Templates are ready and shared once we start the deal. We maintain a verifiable e-signature process with audit trail.
            </motion.p>
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 gap-6"
            >
              <motion.div
                variants={fadeInUp}
                className="bg-card/50 border border-border/50 p-6 rounded-lg shadow-sm"
              >
                <h3 className="text-lg font-semibold mb-3 text-slate-900">Trading Agreements</h3>
                <FeatureBadges
                  features={[
                    { icon: <Shield className="h-4 w-4" />, label: "NDA/NCNDA templates" },
                    { icon: <FileText className="h-4 w-4" />, label: "LOI/ICPO formats" },
                    { icon: <Receipt className="h-4 w-4" />, label: "SCO/FCO structures" },
                    { icon: <Award className="h-4 w-4" />, label: "SPA frameworks" }
                  ]}
                  align="left"
                  size="sm"
                  className="mt-4"
                />
              </motion.div>
              <motion.div
                variants={fadeInUp}
                className="bg-card/50 border border-border/50 p-6 rounded-lg shadow-sm"
              >
                <h3 className="text-lg font-semibold mb-3 text-slate-900">Payment & Logistics</h3>
                <FeatureBadges
                  features={[
                    { icon: <CreditCard className="h-4 w-4" />, label: "LC guidelines & templates" },
                    { icon: <Shield className="h-4 w-4" />, label: "Insurance clauses" },
                    { icon: <Ship className="h-4 w-4" />, label: "Charter Party basics" },
                    { icon: <Users className="h-4 w-4" />, label: "Fixture coordination docs" }
                  ]}
                  align="left"
                  size="sm"
                  className="mt-4"
                />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-4"
            >
              Frequently Asked Questions
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-center text-slate-600 mb-12"
            >
              Common questions about our AI-powered trading platform and services
            </motion.p>
            
            <motion.div variants={fadeInUp}>
              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="item-1" className="bg-white border border-slate-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-slate-900">
                      What is the difference between FOB and CFR/CIF in commodity trading?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    <div className="space-y-3">
                      <p><strong>FOB (Free On Board):</strong> The seller is responsible for delivering goods to the port and loading them onto the vessel. Risk transfers to the buyer once goods cross the ship's rail.</p>
                      <p><strong>CFR (Cost and Freight):</strong> The seller pays for transportation to the destination port, but risk transfers when goods are loaded onto the vessel.</p>
                      <p><strong>CIF (Cost, Insurance, and Freight):</strong> Similar to CFR, but the seller also provides minimum insurance coverage for the goods during transit.</p>
                      <p>Our AI platform helps match FOB sellers with CFR/CIF buyers, optimizing the entire supply chain from origin to destination.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2" className="bg-white border border-slate-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-slate-900">
                      How do Letters of Credit (LC) work in your platform?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    <div className="space-y-3">
                      <p>We support multiple LC types to facilitate secure transactions:</p>
                      <ul className="list-disc pl-6 space-y-2">
                        <li><strong>LC at Sight:</strong> Payment made immediately upon presentation of compliant documents</li>
                        <li><strong>SBLC (Standby Letter of Credit):</strong> Acts as a guarantee, activated if the buyer fails to perform</li>
                        <li><strong>DLC (Documentary Letter of Credit):</strong> Standard LC requiring specific shipping documents</li>
                        <li><strong>Transferable LC:</strong> Allows the beneficiary to transfer credit to suppliers</li>
                        <li><strong>Back-to-Back LC:</strong> Enables traders to use one LC to open another with their supplier</li>
                      </ul>
                      <p>Our platform coordinates with major banks to streamline LC processes and reduce transaction time.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3" className="bg-white border border-slate-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-slate-900">
                      What documents are required for commodity trading?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    <div className="space-y-3">
                      <p>Our platform provides standardized templates for all essential trading documents:</p>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-semibold mb-2">Initial Documents:</h4>
                          <ul className="list-disc pl-4 space-y-1 text-sm">
                            <li>NDA/NCNDA (Non-Disclosure Agreement)</li>
                            <li>LOI (Letter of Intent)</li>
                            <li>ICPO (Indicative Commercial Purchase Order)</li>
                            <li>SCO (Sales Contract Offer)</li>
                            <li>FCO (Firm Contract Offer)</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">Final Documents:</h4>
                          <ul className="list-disc pl-4 space-y-1 text-sm">
                            <li>SPA (Sales and Purchase Agreement)</li>
                            <li>COO (Certificate of Origin)</li>
                            <li>Inspection certificates (SGS/Cotecna)</li>
                            <li>Bill of Lading</li>
                            <li>Insurance certificates</li>
                          </ul>
                        </div>
                      </div>
                      <p className="text-sm text-slate-500 mt-3">All templates are private and shared once we initiate a deal. We maintain verifiable e-signature processes with full audit trails.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4" className="bg-white border border-slate-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-slate-900">
                      How does your AI matching system work?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    <div className="space-y-3">
                      <p>Our AI-powered matching system analyzes multiple factors to connect buyers and sellers:</p>
                      <ul className="list-disc pl-6 space-y-2">
                        <li><strong>Commodity Specifications:</strong> Grade, quality, and HS code matching</li>
                        <li><strong>Volume Requirements:</strong> Minimum lot sizes and delivery schedules</li>
                        <li><strong>Geographic Optimization:</strong> Origin-destination routing for cost efficiency</li>
                        <li><strong>Payment Terms:</strong> LC preferences and bank relationships</li>
                        <li><strong>Market Conditions:</strong> Real-time pricing and freight rates</li>
                        <li><strong>Risk Assessment:</strong> KYC/AML compliance and sanctions screening</li>
                      </ul>
                      <p>The system provides predictive signals on pricing trends, seasonality patterns, and freight sensitivity to help optimize deal timing and margins.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-5" className="bg-white border border-slate-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-slate-900">
                      What is KYC/AML and sanctions screening?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    <div className="space-y-3">
                      <p><strong>KYC (Know Your Customer):</strong> Verification process to identify and verify the identity of trading partners, including company registration, beneficial ownership, and business legitimacy.</p>
                      <p><strong>AML (Anti-Money Laundering):</strong> Procedures to detect and prevent money laundering activities through transaction monitoring and suspicious activity reporting.</p>
                      <p><strong>Sanctions Screening:</strong> Checks against international sanctions lists (OFAC, EU, UN) to ensure compliance with global trade restrictions.</p>
                      <p>All participants on our platform undergo comprehensive screening before any transactions are facilitated. This ensures regulatory compliance and reduces counterparty risk.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-6" className="bg-white border border-slate-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-slate-900">
                      How do you handle logistics and shipping coordination?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    <div className="space-y-3">
                      <p>Our platform coordinates with verified shipbrokers and logistics partners to handle:</p>
                      <ul className="list-disc pl-6 space-y-2">
                        <li><strong>Vessel Chartering:</strong> Charter Party negotiations and fixture coordination</li>
                        <li><strong>Port Operations:</strong> Loading and discharge port arrangements</li>
                        <li><strong>Laytime Management:</strong> Optimizing loading/unloading schedules to minimize demurrage costs</li>
                        <li><strong>Documentation:</strong> Bill of Lading, shipping instructions, and customs documentation</li>
                        <li><strong>Insurance:</strong> Cargo and marine insurance coordination</li>
                        <li><strong>Inspection Services:</strong> SGS, Cotecna, and other inspection company coordination</li>
                      </ul>
                      <p>We maintain relationships with major shipbrokers and logistics providers to ensure smooth execution from origin to destination.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-7" className="bg-white border border-slate-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-slate-900">
                      What are the typical deal sizes and commodities you handle?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    <div className="space-y-3">
                      <p>Our platform facilitates deals across various commodity categories:</p>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-semibold mb-2">Agricultural Commodities:</h4>
                          <ul className="list-disc pl-4 space-y-1 text-sm">
                            <li>Grains (Wheat, Corn, Rice)</li>
                            <li>Oilseeds (Soybeans, Sunflower)</li>
                            <li>Sugar and Coffee</li>
                            <li>Palm Oil and Derivatives</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2">Industrial Commodities:</h4>
                          <ul className="list-disc pl-4 space-y-1 text-sm">
                            <li>Metals (Copper, Aluminum, Steel)</li>
                            <li>Minerals (Iron Ore, Coal)</li>
                            <li>Chemicals and Fertilizers</li>
                            <li>Energy Products</li>
                          </ul>
                        </div>
                      </div>
                      <p><strong>Deal Sizes:</strong> Typically range from 1,000 MT to 100,000+ MT, depending on commodity type and market conditions. Our AI system optimizes lot sizes based on vessel capacity and market liquidity.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-8" className="bg-white border border-slate-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-slate-900">
                      How do you ensure transaction security and compliance?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    <div className="space-y-3">
                      <p>Security and compliance are fundamental to our platform:</p>
                      <ul className="list-disc pl-6 space-y-2">
                        <li><strong>Bank-Grade Security:</strong> All transactions are subject to final contract and inspection</li>
                        <li><strong>Verifiable E-Signatures:</strong> Digital signature process with complete audit trail</li>
                        <li><strong>Document Verification:</strong> Automated checks for document authenticity and compliance</li>
                        <li><strong>Payment Security:</strong> LC-backed transactions with major international banks</li>
                        <li><strong>Regulatory Compliance:</strong> Full adherence to international trade regulations and sanctions</li>
                        <li><strong>Risk Management:</strong> Continuous monitoring of counterparty risk and market conditions</li>
                      </ul>
                      <p>We maintain relationships with top-tier banks, insurance providers, and inspection companies to ensure the highest standards of security and compliance.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-9" className="bg-white border border-slate-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-slate-900">
                      What makes your platform different from traditional commodity trading?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    <div className="space-y-3">
                      <p>Our AI-powered platform revolutionizes commodity trading through:</p>
                      <ul className="list-disc pl-6 space-y-2">
                        <li><strong>Predictive Intelligence:</strong> AI-driven market signals and trend analysis</li>
                        <li><strong>Automated Matching:</strong> Smart buyer-seller pairing based on multiple criteria</li>
                        <li><strong>Standardized Documentation:</strong> Pre-approved templates reducing negotiation time</li>
                        <li><strong>Real-Time Optimization:</strong> Dynamic pricing and logistics optimization</li>
                        <li><strong>Risk Mitigation:</strong> Comprehensive screening and compliance checks</li>
                        <li><strong>Transparency:</strong> Full audit trail and transaction tracking</li>
                        <li><strong>Efficiency:</strong> Reduced time-to-market and lower transaction costs</li>
                      </ul>
                      <p>Traditional trading relies on manual processes and personal networks. Our platform combines human expertise with AI capabilities to create a more efficient, transparent, and profitable trading environment.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-10" className="bg-white border border-slate-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-semibold text-slate-900">
                      How do I get started with your trading platform?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    <div className="space-y-3">
                      <p>Getting started is simple and secure:</p>
                      <ol className="list-decimal pl-6 space-y-2">
                        <li><strong>Submit Your Requirements:</strong> Complete our producer, buyer, or trader form with your specific needs</li>
                        <li><strong>KYC Verification:</strong> Provide company documentation and undergo compliance screening</li>
                        <li><strong>Platform Access:</strong> Receive access to our AI matching system and document templates</li>
                        <li><strong>Deal Discovery:</strong> Our AI identifies potential matches and provides market intelligence</li>
                        <li><strong>Transaction Execution:</strong> Execute deals with standardized documentation and secure payments</li>
                      </ol>
                      <p>Our team provides personalized support throughout the process, from initial setup to deal completion. All relationships and partnerships are built on trust, transparency, and mutual success.</p>
                      <p className="text-sm text-slate-500 mt-3">Ready to start? <a href="mailto:michel@padmora.com" className="text-blue-600 hover:underline">Send enquiry</a> to discuss your specific trading needs.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Start a Deal CTA */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-2xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-slate-900 mb-6"
            >
              Ready to Connect?
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-slate-600 mb-8"
            >
              Send an enquiry to michel@padmora.com to discuss your commodity trading needs.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Button
                size="lg"
                variant="default"
                className="px-8 py-4 text-lg bg-primary hover:bg-primary/90 text-white"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com"
                >
                  Send enquiry
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Trading;
